import React, { useMemo, useState } from "react";
import {
  X,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { entityKindMap } from "../../utils";
import { EntityKind } from "../../Dashboard/types";
import {
  downloadImportTemplate,
  ImportResponse,
  ImportRow,
  ImportSummaryGroup,
  importSpreadsheet,
} from "./importApi";

const SUMMARY_PREVIEW = 8;

// The records the import creates beyond the rows, and the names several
// rows share, most rows first: a generic value stands out here before
// anyone confirms (#12132).
const SummaryList: React.FC<{
  title: string;
  groups: ImportSummaryGroup[];
  describe: (g: ImportSummaryGroup) => string;
}> = ({ title, groups, describe }) => {
  const [showAll, setShowAll] = useState(false);
  if (groups.length === 0) return null;
  const shown = showAll ? groups : groups.slice(0, SUMMARY_PREVIEW);
  return (
    <div className="rounded-[10px] border border-gray-200 px-3 py-2">
      <div className="text-sm font-medium text-gray-800">{title}</div>
      <ul className="mt-1 flex flex-col gap-0.5">
        {shown.map((g) => (
          <li
            key={`${g.kind}:${g.name}`}
            className="text-sm text-[#10141B] flex items-baseline gap-2"
          >
            <span className="font-mono text-xs text-[#6E7480] w-14 shrink-0 text-right">
              {g.rows} row{g.rows === 1 ? "" : "s"}
            </span>
            <span className="truncate">{g.name}</span>
            <span className="text-xs text-[#6E7480] truncate">
              {describe(g)}
            </span>
          </li>
        ))}
      </ul>
      {groups.length > SUMMARY_PREVIEW && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 text-xs text-[#6E7480] hover:text-[#10141B] cursor-pointer"
        >
          {showAll ? "Show fewer" : `Show all ${groups.length}`}
        </button>
      )}
    </div>
  );
};

// Kinds a spreadsheet import may create — mirrors IMPORTABLE_KINDS on the
// backend. Interactions have their own creation flow.
const IMPORTABLE_KINDS: EntityKind[] = [
  "manager",
  "vehicle",
  "company",
  "person",
  "topic",
  "client",
];

interface EntityImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported?: () => void;
}

type Step = "upload" | "preview" | "done";

const statusIcon = (status: ImportRow["status"]) => {
  if (status === "ok") {
    return <CheckCircle2 size={16} className="text-green-600 shrink-0" />;
  }
  if (status === "warning") {
    return <AlertTriangle size={16} className="text-amber-500 shrink-0" />;
  }
  if (status === "update") {
    return <RefreshCw size={16} className="text-blue-600 shrink-0" />;
  }
  return <XCircle size={16} className="text-red-600 shrink-0" />;
};

const EntityImportModal: React.FC<EntityImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<EntityKind | "auto">("auto");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);

  const counts = result?.counts ?? {};
  // A merged row (a repeat of an earlier row) shows as a warning but
  // creates nothing of its own.
  const creatableCount =
    (counts["ok"] ?? 0) + (counts["warning"] ?? 0) - (counts["merged"] ?? 0);
  const updateCount = counts["update"] ?? 0;
  const actionableCount = creatableCount + updateCount;
  // A row with an error is skipped on confirm and the rest of the file goes
  // in. Only a sheet the import cannot read at all blocks the confirm.
  const errorRowCount = counts["error"] ?? 0;
  const hasSheetErrors = (result?.sheets ?? []).some(
    (s) => !s.ignored && s.errors.length > 0
  );

  const rowsBySheet = useMemo(
    () =>
      (result?.sheets ?? []).filter(
        (s) => s.rows.length > 0 || s.ignored || s.errors.length > 0
      ),
    [result]
  );

  const reset = () => {
    setStep("upload");
    setFile(null);
    setKind("auto");
    setBusy(false);
    setError(null);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setError(null);
    try {
      const blob = await downloadImportTemplate(kind === "auto" ? null : kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not generate the template"
      );
    }
  };

  const runImport = async (create: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const response = await importSpreadsheet(file, {
        kind: kind === "auto" ? null : kind,
        create,
      });
      setResult(response);
      if (create && response.committed) {
        setStep("done");
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "rms",
        });
        onImported?.();
      } else {
        setStep("preview");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "The import failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-3xl relative max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Import from Spreadsheet
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === "upload" && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">
              Upload an Excel or CSV file. Each sheet becomes a set of new
              records — either pick what the file contains, or name your sheets
              after what they hold (e.g. &quot;Managers&quot;,
              &quot;Funds&quot;). You&apos;ll see a preview of everything before
              anything is created.
            </p>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              Spreadsheet file
              <input
                aria-label="Spreadsheet file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              What does the file contain?
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as EntityKind | "auto")}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="auto">Detect from sheet names</option>
                {IMPORTABLE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {entityKindMap[k].displayLabel}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Download template
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => runImport(false)}
                  disabled={!file || busy}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white text-sm disabled:opacity-50"
                >
                  {busy ? "Reading…" : "Preview"}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "preview" && result && (
          <div className="flex flex-col gap-3 overflow-hidden">
            <div className="text-sm text-gray-600">
              {counts["total"] ?? 0} rows — {counts["ok"] ?? 0} ready,{" "}
              {counts["warning"] ?? 0} with warnings,
              {updateCount > 0 &&
                ` ${updateCount} updating existing records,`}{" "}
              {errorRowCount} with errors.
              {hasSheetErrors
                ? " Fix the sheet problem in the file and upload it again to continue."
                : errorRowCount > 0 &&
                  " Rows with errors are skipped; fix them in the file and import it again later."}
            </div>
            <div className="overflow-y-auto flex-1 flex flex-col gap-4">
              <SummaryList
                title={`Will also create ${
                  result.summary.references_to_create.length
                } record${
                  result.summary.references_to_create.length === 1 ? "" : "s"
                } named in the file`}
                groups={result.summary.references_to_create}
                describe={(g) =>
                  entityKindMap[g.kind as EntityKind]?.displayLabel ?? g.kind
                }
              />
              <SummaryList
                title="Names shared by several rows"
                groups={result.summary.shared_names}
                describe={(g) =>
                  g.outcome === "merged"
                    ? "same record, merged"
                    : `renamed per manager: ${g.managers.slice(0, 4).join(", ")}${
                        g.managers.length > 4
                          ? ` and ${g.managers.length - 4} more`
                          : ""
                      }`
                }
              />
              {rowsBySheet.map((sheet, i) => (
                <div key={sheet.sheet_name ?? i}>
                  {sheet.sheet_name && (
                    <div className="text-sm font-medium text-gray-800 mb-1">
                      {sheet.sheet_name}
                      {sheet.kind && (
                        <span className="text-gray-500 font-normal">
                          {" "}
                          —{" "}
                          {entityKindMap[sheet.kind as EntityKind]
                            ?.displayLabel ?? sheet.kind}
                        </span>
                      )}
                    </div>
                  )}
                  {sheet.errors.map((sheetError) => (
                    <div
                      key={sheetError}
                      className={`text-sm mb-1 ${
                        sheet.ignored ? "text-gray-500" : "text-red-700"
                      }`}
                    >
                      {sheetError}
                    </div>
                  ))}
                  {sheet.rows.length > 0 && (
                    <table className="w-full text-sm">
                      <tbody>
                        {sheet.rows.map((row) => (
                          <tr
                            key={row.row_number}
                            className="border-b border-gray-100 align-top"
                          >
                            <td className="py-1.5 pr-2 text-gray-400 w-10">
                              {row.row_number}
                            </td>
                            <td className="py-1.5 pr-2 w-5">
                              {statusIcon(row.status)}
                            </td>
                            <td className="py-1.5 pr-3 font-medium text-gray-900 whitespace-nowrap">
                              {row.name}
                            </td>
                            <td className="py-1.5 text-gray-600">
                              {row.messages.map((m, j) => (
                                <div
                                  key={j}
                                  className={
                                    m.severity === "error"
                                      ? "text-red-700"
                                      : "text-amber-700"
                                  }
                                >
                                  {m.text}
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => {
                  setStep("upload");
                  setResult(null);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => runImport(true)}
                disabled={busy || hasSheetErrors || actionableCount === 0}
                className="px-4 py-2 rounded-md bg-gray-800 text-white text-sm disabled:opacity-50"
              >
                {busy
                  ? "Importing…"
                  : (updateCount > 0
                      ? `Create ${creatableCount}, update ${updateCount}`
                      : `Create ${creatableCount} record${
                          creatableCount === 1 ? "" : "s"
                        }`) +
                    (errorRowCount > 0 ? `, skip ${errorRowCount}` : "")}
              </button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={20} />
              <span className="text-sm font-medium">
                {counts["created"] ?? 0} record
                {(counts["created"] ?? 0) === 1 ? "" : "s"} created
                {(counts["updated"] ?? 0) > 0 &&
                  `, ${counts["updated"]} updated`}
                {(counts["references_created"] ?? 0) > 0 &&
                  `, plus ${counts["references_created"]} referenced record${
                    counts["references_created"] === 1 ? "" : "s"
                  }`}
              </span>
            </div>
            {(counts["skipped"] ?? 0) > 0 && (
              <div className="text-sm text-gray-600">
                {counts["skipped"]} row{counts["skipped"] === 1 ? "" : "s"} with
                errors {counts["skipped"] === 1 ? "was" : "were"} skipped. Fix{" "}
                {counts["skipped"] === 1 ? "it" : "them"} in the file and import
                it again; rows already imported are updated, not duplicated.
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-md bg-gray-800 text-white text-sm"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityImportModal;
